import { Container } from "../../types/Container";
import { TerminalCall } from "../../types/TerminalCall";
import { Vessel } from "../../types/Vessel";
import { normalizeId } from "./normalize";

const WEIGHT_X_AXIS_TYPE = "x_axis";

export const sideWeights = (containerWeights: number[] = []) => {
  if (!Array.isArray(containerWeights) || containerWeights.length === 0) {
    return { starboard: 0, port: 0 };
  }

  const n = containerWeights.length;
  const mid = Math.floor(n / 2);

  const sum = (arr: number[]) =>
    arr.reduce((acc, v) => acc + (Number.isFinite(v) ? v : 0), 0);

  let starboardKg = 0;
  let portKg = 0;

  if (n % 2 === 0) {
    starboardKg = sum(containerWeights.slice(0, mid));
    portKg = sum(containerWeights.slice(mid));
  } else {
    const middle = Number.isFinite(containerWeights[mid])
      ? containerWeights[mid]
      : 0;

    starboardKg = sum(containerWeights.slice(0, mid)) + middle / 2;
    portKg = sum(containerWeights.slice(mid + 1)) + middle / 2;
  }

  const toTonnes = (kg: number) => (kg > 0 ? Math.round(kg / 1000) : 0);

  return {
    starboard: toTonnes(starboardKg),
    port: toTonnes(portKg),
  };
};

export const containerGroupedByAxis = (
  vessel: Vessel,
  containers: any,
  terminalCall: TerminalCall | undefined,
  axisType: string
) => {
  let containerGroupedByAxis = [];

  if (!vessel || !vessel.positions || terminalCall === undefined) {
    return "";
  }

  const vesselId = normalizeId(vessel["@id"]);
  let maxNumberAxis = Math.max(...vessel.positions[0]) ?? 0;
  let position = 2;

  if (axisType === WEIGHT_X_AXIS_TYPE) {
    maxNumberAxis =
      vessel && vessel.positions && vessel.positions.length > 0
        ? vessel.positions[0].length
        : 0;
    position = 1;
  }

  for (let i = 1; i <= maxNumberAxis; i++) {
    containerGroupedByAxis[i] = containers
      .filter(
        (container: Container) =>
          container.position !== undefined &&
          container.position[position] === i &&
          container.position[3] === vesselId
      )
      .reduce((prev, current) => {
        return prev + current.weight;
      }, 0);
  }

  return containerGroupedByAxis.filter(function () {
    return true;
  });
};

export interface DraughtRow {
  weight: number;
  draught: number;
}

export function getDraughtForTotalWeight(
  totalWeight: number,
  waterType: string,
  weightDraughtTable?: Record<string, DraughtRow[]>
): number | null {
  const table = weightDraughtTable?.[waterType];
  if (!table || table.length === 0) return null;

  const sorted = [...table].sort((a, b) => b.weight - a.weight);

  for (const row of sorted) {
    if (totalWeight > row.weight) {
      return row.draught;
    }
  }

  return null;
}

export function getHighestDraughtForWaterType(
  waterType: string,
  weightDraughtTable?: Record<string, DraughtRow[]>
): number | null {
  const table = weightDraughtTable?.[waterType];

  if (!table || table.length === 0) return null;

  return table.reduce(
    (max, row) => (row.draught > max ? row.draught : max),
    table[0].draught
  );
}

export const getContainerTotalWeights = (containerWeights: any) => {
  const newContainerWeights = [...containerWeights];
  const arrayRange = Math.round(containerWeights.length / 3);

  const formattedContainerWeights = new Array(Math.ceil(arrayRange))
    .fill()
    .map((_) => newContainerWeights.splice(0, arrayRange));
  const totalContainerWeights = [];
  let rangeMin = 1;

  formattedContainerWeights.slice(0, 3).map((weights: any, index: any) => {
    rangeMin = index === 0 ? rangeMin : rangeMin + weights.length;
    let rangeMax = ++index * weights.length;

    totalContainerWeights.push({
      total: weights.reduce((accumulator, weight) => accumulator + weight, 0),
      range: `${rangeMin} - ${rangeMax}`,
    });
  });

  return totalContainerWeights;
};